import { showFormMessage } from './showFormMessage.ts';
import { submitForm } from './submitForm.ts';
import { FormData } from './interfaces/FormData.ts';

// Validation functions
const validateEmail = (email: string): boolean => {
  // Improved email validation: RFC 5322 compliant (simplified)
  // Allows most common email formats while being strict enough
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

  // Additional checks
  if (!email || email.length > 254) return false; // RFC 5321 limit
  if (email.startsWith('.') || email.endsWith('.')) return false;
  if (email.includes('..')) return false; // No consecutive dots
  if (email.split('@').length !== 2) return false; // Exactly one @

  const [localPart, domain] = email.split('@');
  if (localPart.length > 64) return false; // RFC 5321 limit
  if (domain.length > 253) return false; // RFC 5321 limit
  if (!domain.includes('.')) return false; // Domain must have TLD

  return emailRegex.test(email);
};

const validatePhone = (phone: string): boolean => {
  // Russian phone format: +7 (XXX) XXX-XX-XX
  // Remove all formatting characters
  const digits = phone.replace(/\D/g, '');

  // Must be 11 digits (7 + 10 digits) or 10 digits (without country code)
  if (digits.length === 10) {
    // 10 digits: assume it's a number without country code, add 7
    return /^\d{10}$/.test(digits);
  } else if (digits.length === 11) {
    // 11 digits: must start with 7 or 8
    if (digits[0] === '7' || digits[0] === '8') {
      return /^[78]\d{10}$/.test(digits);
    }
    return false;
  }

  return false;
};

// Show error message
const showError = (input: HTMLElement, message: string): void => {
  const formGroup = input.closest('.form-group');
  if (!formGroup) return;

  const errorElement = formGroup.querySelector('.form-error') as HTMLElement;
  if (errorElement) {
    errorElement.textContent = message;
    errorElement.setAttribute('aria-live', 'polite');
  }

  input.setAttribute('aria-invalid', 'true');
  input.classList.add('error');
};

// Clear error message
const clearError = (input: HTMLElement): void => {
  const formGroup = input.closest('.form-group');
  if (!formGroup) return;

  const errorElement = formGroup.querySelector('.form-error') as HTMLElement;
  if (errorElement) {
    errorElement.textContent = '';
  }

  input.removeAttribute('aria-invalid');
  input.classList.remove('error');
};

// Validate single field
const validateField = (
  input: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
): boolean => {
  const value = input.type === 'checkbox' ? (input as HTMLInputElement).checked : input.value.trim();
  const name = input.name;
  const required = input.hasAttribute('required');

  // Clear previous error
  clearError(input);

  // Check required fields
  if (required && !value) {
    showError(input, 'Это поле обязательно для заполнения');
    return false;
  }

  // Validate email
  if (name === 'email' && value && !validateEmail(value.toString())) {
    showError(input, 'Введите корректный e-mail адрес');
    return false;
  }

  // Validate phone
  if (name === 'phone' && value && !validatePhone(value.toString())) {
    showError(input, 'Введите корректный номер телефона');
    return false;
  }

  return true;
};

// Initialize form validation
export function initFormValidation(): void {
  const forms = document.querySelectorAll('form[id]') as NodeListOf<HTMLFormElement>;

  forms.forEach((form) => {
    // Record form start time
    form.dataset.startTime = Date.now().toString();

    // Flag to prevent double submission
    let isSubmitting = false;

    // Get all inputs
    const inputs = form.querySelectorAll('input, textarea, select') as NodeListOf<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >;

    // Validate on blur
    inputs.forEach((input) => {
      input.addEventListener('blur', () => {
        validateField(input);
      });

      // Clear error on input
      input.addEventListener('input', () => {
        if (input.classList.contains('error')) {
          clearError(input);
        }
      });

      // Real-time validation for email (on input, not just blur)
      if (input.name === 'email' && input.type === 'email') {
        input.addEventListener('input', () => {
          const value = (input as HTMLInputElement).value.trim();
          if (value && !validateEmail(value)) {
            // Don't show error immediately, wait for blur or submit
            // Just mark as potentially invalid
          }
        });
      }
    });

    const submitButton = form.querySelector('button[type="submit"]') as HTMLButtonElement | null;

    // Handle form submission
    form.addEventListener('submit', async (e) => {
      // Prevent double submission
      if (isSubmitting) {
        e.preventDefault();
        return;
      }
      e.preventDefault();

      // Validate all fields first
      let isValid = true;
      inputs.forEach((input) => {
        if (!validateField(input)) {
          isValid = false;
        }
      });

      if (!isValid) {
        showFormMessage(form, 'Пожалуйста, исправьте ошибки в форме', 'error');
        return;
      }

      isSubmitting = true;
      try {
        await handleFormSubmit(form, inputs, submitButton);
      } finally {
        isSubmitting = false;
      }
    });
  });
}

// Handle form submission (extracted to avoid duplication)
async function handleFormSubmit(
  form: HTMLFormElement,
  inputs: NodeListOf<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  submitButton: HTMLButtonElement | null
): Promise<void> {
  // Validate all fields
  let isValid = true;
  inputs.forEach((input) => {
    if (!validateField(input)) {
      isValid = false;
    }
  });

  if (!isValid) {
    showFormMessage(form, 'Пожалуйста, исправьте ошибки в форме', 'error');
    return;
  }

  // Collect form data
  const formData: FormData = { email: '' };
  inputs.forEach((input) => {
    if (input.type === 'checkbox') {
      formData[input.name as keyof FormData] = (input as HTMLInputElement).checked as never;
    } else if (input.type === 'select-one') {
      formData[input.name as keyof FormData] = (input as HTMLSelectElement).options[(input as HTMLSelectElement).selectedIndex].text as never;
    } else if (input.name && input.value) {
      let value = input.value;

      // Normalize phone number: remove formatting, ensure +7 format
      if (input.name === 'phone' && input.type === 'tel') {
        const digits = value.replace(/\D/g, '');
        if (digits.length === 10) {
          value = '+7' + digits;
        } else if (digits.length === 11 && digits[0] === '8') {
          value = '+7' + digits.slice(1);
        } else if (digits.length === 11 && digits[0] === '7') {
          value = '+' + digits;
        } else {
          value = value; // Keep as is if already formatted
        }
      }

      formData[input.name as keyof FormData] = value as never;
    }
  });

  // Disable submit button
  if (submitButton) {
    submitButton.disabled = true;
    const originalText = submitButton.textContent;
    submitButton.textContent = 'Отправка...';
    submitButton.dataset.originalText = originalText || '';
  }

  try {
    // Submit form
    await submitForm(form, formData);
  } finally {
    // Re-enable button
    if (submitButton) {
      submitButton.disabled = false;
      submitButton.textContent = submitButton.dataset.originalText || 'Отправить заявку';
    }
  }
}
